import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  deleteDeliveryChargeApi,
  getDeliveryChargeApi,
  getDeliveryChargesApi,
  postDeliveryChargeApi,
  updateDeliveryChargeApi,
} from "../../api/ecommerce/DeliveryChargeApi";

const initialState = {
  deliveryCharge: null,
  deliveryCharges: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
};

const getErrorPayload = (err) => ({
  errorMessage:
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    "Something went wrong",
});

export const getDeliveryCharge = createAsyncThunk(
  "deliveryCharge/getDeliveryCharge",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getDeliveryChargeApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const getDeliveryCharges = createAsyncThunk(
  "deliveryCharge/getDeliveryCharges",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await getDeliveryChargesApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const postDeliveryCharge = createAsyncThunk(
  "deliveryCharge/postDeliveryCharge",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await postDeliveryChargeApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const updateDeliveryCharge = createAsyncThunk(
  "deliveryCharge/updateDeliveryCharge",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateDeliveryChargeApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

export const deleteDeliveryCharge = createAsyncThunk(
  "deliveryCharge/deleteDeliveryCharge",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await deleteDeliveryChargeApi(payload);
      return response?.data;
    } catch (err) {
      return rejectWithValue(getErrorPayload(err));
    }
  }
);

const deliveryChargeSlice = createSlice({
  name: "deliveryCharge",
  initialState,
  reducers: {
    clearDeliveryChargeError: (state) => {
      state.error = null;
    },
    clearDeliveryChargeState: (state) => {
      state.deliveryCharge = null;
      state.deliveryCharges = [];
      state.loading = false;
      state.saving = false;
      state.deleting = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDeliveryCharge.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDeliveryCharge.fulfilled, (state, action) => {
        state.deliveryCharge = action?.payload?.response ?? action?.payload;
        state.loading = false;
      })
      .addCase(getDeliveryCharge.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(getDeliveryCharges.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDeliveryCharges.fulfilled, (state, action) => {
        state.deliveryCharges = action?.payload?.response ?? action?.payload;
        state.loading = false;
      })
      .addCase(getDeliveryCharges.rejected, (state, action) => {
        state.loading = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(postDeliveryCharge.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(postDeliveryCharge.fulfilled, (state, action) => {
        state.deliveryCharge = action?.payload?.response ?? action?.payload;
        state.saving = false;
      })
      .addCase(postDeliveryCharge.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(updateDeliveryCharge.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateDeliveryCharge.fulfilled, (state, action) => {
        state.deliveryCharge = action?.payload?.response ?? action?.payload;
        state.saving = false;
      })
      .addCase(updateDeliveryCharge.rejected, (state, action) => {
        state.saving = false;
        state.error = action?.payload?.errorMessage || null;
      })
      .addCase(deleteDeliveryCharge.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteDeliveryCharge.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(deleteDeliveryCharge.rejected, (state, action) => {
        state.deleting = false;
        state.error = action?.payload?.errorMessage || null;
      });
  },
});

export const { clearDeliveryChargeError, clearDeliveryChargeState } =
  deliveryChargeSlice.actions;

export default deliveryChargeSlice.reducer;
